/**
 * Room page (T052).
 *
 * Owns the RoomClient instance for as long as the route is mounted:
 *   - On mount: roomExists() pre-flight; if 404 → renders "Room not found".
 *     Otherwise: if no remembered name → render NameGate; once submitted,
 *     instantiate RoomClient, call join(), render the video grid.
 *   - On unmount (browser Back, in-app nav): cleanup runs RoomClient.leave()
 *     so devices are released even without the explicit Leave button.
 *
 * Errors:
 *   - ROOM_NOT_FOUND (404 pre-flight or SignalingError) → "Room not found"
 *   - ROOM_FULL  (SignalingError code === 'ROOM_FULL')   → "Room is full"
 *   - Any other failure                                  → generic banner
 *
 * Phase 4 additions (US2):
 *   - ConnectionStatus banner (T069/T070)
 *   - Permission-denied panel with retryLocalMedia (T066/T067)
 *
 * 002-friendly-guest-names (T007, T009, T015):
 *   - NameGate shown when readDisplayName() is empty.
 *   - Typed names are persisted via writeDisplayName; guest names are not.
 *   - Local tile label comes from the store's localDisplayName (authoritative
 *     server value) with fallback to the remembered stored name.
 */
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useNavigate, useParams } from 'react-router'
import { useQuery } from '@tanstack/react-query'
import { useTranslation } from 'react-i18next'
import { useShallow } from 'zustand/react/shallow'
import { roomExists } from '../lib/api.js'
import { readDisplayName, writeDisplayName } from '../lib/displayName.js'
import { env } from '../lib/env.js'
import { RoomClient } from '../room/RoomClient.js'
import { SignalingError } from '../room/signaling.js'
import { useRoomStore } from '../room/store.js'
import { VideoGrid } from '../components/VideoGrid.js'
import { Controls } from '../components/Controls.js'
import { ConnectionStatus } from '../components/ConnectionStatus.js'
import { NameGate } from '../components/NameGate.js'
import styles from './Room.module.css'

type Blocker =
  | { kind: 'checking' }
  | { kind: 'notFound' }
  | { kind: 'full' }
  | { kind: 'error'; message: string }
  | null

/**
 * When the visitor has no remembered name, the gate is shown until they
 * submit. We track whether joining has been kicked off with this state.
 */
interface JoinParams {
  displayName: string
  nameSource: 'typed' | 'guest'
}

export function Room() {
  const { id = '' } = useParams<{ id: string }>()
  const navigate = useNavigate()
  const { t } = useTranslation()

  const existsQuery = useQuery({
    queryKey: ['roomExists', id],
    queryFn: () => roomExists(id),
    enabled: !!id,
    staleTime: 0,
    retry: false,
  })

  const [blocker, setBlocker] = useState<Blocker>({ kind: 'checking' })
  const [toast, setToast] = useState<string | null>(null)
  const [retrying, setRetrying] = useState(false)
  const clientRef = useRef<RoomClient | null>(null)

  // Remembered name from localStorage (non-empty → skip gate).
  const rememberedName = readDisplayName()

  // Gate state: null means "not yet decided / use remembered name".
  // Non-null means the gate was shown and the user submitted.
  const [joinParams, setJoinParams] = useState<JoinParams | null>(
    rememberedName ? { displayName: rememberedName, nameSource: 'typed' } : null,
  )

  // Whether to show the NameGate (room exists, but no name yet).
  const showGate = existsQuery.data === true && joinParams === null

  // Callback from NameGate — persist typed names only (T009).
  const onGateSubmit = useCallback(
    (name: string, source: 'typed' | 'guest') => {
      if (source === 'typed') {
        writeDisplayName(name)
      }
      setJoinParams({ displayName: name, nameSource: source })
    },
    [],
  )

  // Once we know the room exists AND have join params, kick off RoomClient.join.
  useEffect(() => {
    if (!id) return
    if (existsQuery.isLoading) {
      setBlocker({ kind: 'checking' })
      return
    }
    if (existsQuery.isError) {
      setBlocker({ kind: 'error', message: t('room.joiningError') })
      return
    }
    if (existsQuery.data === false) {
      setBlocker({ kind: 'notFound' })
      return
    }

    // Room exists — wait until name is provided.
    if (!joinParams) {
      // Gate will be rendered — clear the checking blocker.
      setBlocker(null)
      return
    }

    // exists === true AND name is ready: join
    setBlocker(null)
    const { displayName, nameSource } = joinParams
    const client = new RoomClient({ wsUrl: env.WS_URL })
    clientRef.current = client

    client.join(id, displayName, nameSource).catch((err: unknown) => {
      if (err instanceof SignalingError) {
        if (err.error.code === 'ROOM_NOT_FOUND') {
          setBlocker({ kind: 'notFound' })
          return
        }
        if (err.error.code === 'ROOM_FULL') {
          setBlocker({ kind: 'full' })
          return
        }
      }
      setBlocker({ kind: 'error', message: t('room.joiningError') })
    })

    // Cleanup on unmount: release devices + close WS even if user used the
    // browser back button instead of an in-app Leave control.
    return () => {
      client.leave()
      clientRef.current = null
    }
  }, [id, existsQuery.isLoading, existsQuery.isError, existsQuery.data, joinParams, t])

  // IMPORTANT: don't call Object.values inside the selector — it would
  // return a fresh array on every store read, so zustand's useSyncExternal-
  // Store would think state changed every time and re-render forever
  // ("Maximum update depth exceeded" / "getSnapshot should be cached").
  // Select stable references, derive the array in a useMemo.
  const {
    peersMap,
    localStream,
    localPeerId,
    localDisplayName,
    localMediaState,
    localMediaError,
    connectionStatus,
    videoInputCount,
  } = useRoomStore(
    useShallow((s) => ({
      peersMap: s.peers,
      localStream: s.localStream,
      localPeerId: s.localPeerId,
      localDisplayName: s.localDisplayName,
      localMediaState: s.localMediaState,
      localMediaError: s.localMediaError,
      connectionStatus: s.connectionStatus,
      videoInputCount: s.videoInputCount,
    })),
  )
  const peers = useMemo(() => Object.values(peersMap), [peersMap])

  // Derive the label for the local tile:
  // 1. Authoritative server name (after join response sets it)
  // 2. The name supplied at gate/join time
  // 3. The remembered name from localStorage
  // Never fall back to the hardcoded 'Guest' string.
  const localLabel =
    localDisplayName ?? joinParams?.displayName ?? rememberedName

  async function onCopyLink() {
    try {
      await navigator.clipboard.writeText(window.location.href)
      setToast(t('room.linkCopied'))
      setTimeout(() => setToast(null), 2000)
    } catch {
      // Clipboard rejected (insecure context, permission). No-op for MVP.
    }
  }

  async function onRetryMedia() {
    const client = clientRef.current
    if (!client) return
    setRetrying(true)
    try {
      await client.retryLocalMedia()
    } finally {
      setRetrying(false)
    }
  }

  function onToggleMic() {
    clientRef.current?.setMicEnabled(!localMediaState.mic)
  }

  function onToggleCam() {
    clientRef.current?.setCamEnabled(!localMediaState.cam)
  }

  async function onSwitchCamera() {
    const client = clientRef.current
    if (!client) return
    try {
      await client.switchCamera()
    } catch {
      setToast(t('controls.switchCameraFailed'))
      setTimeout(() => setToast(null), 4000)
    }
  }

  function onLeave() {
    // Call leave() immediately so devices are released on click; the
    // subsequent navigate('/') will unmount Room and the useEffect cleanup
    // will call leave() again — which is safe because leave() is idempotent.
    clientRef.current?.leave()
    navigate('/')
  }

  if (blocker?.kind === 'notFound' || blocker?.kind === 'full') {
    const title = blocker.kind === 'notFound' ? t('room.notFoundTitle') : t('room.fullTitle')
    const help = blocker.kind === 'notFound' ? t('room.notFoundHelp') : t('room.fullHelp')
    return (
      <main className={styles.shell}>
        <div className={styles.statusOverlay}>
          <div className={styles.statusCard}>
            <h2>{title}</h2>
            <p>{help}</p>
            <a
              href="/"
              onClick={(e) => {
                e.preventDefault()
                navigate('/')
              }}
            >
              {t('room.backToLanding')}
            </a>
          </div>
        </div>
      </main>
    )
  }

  return (
    <main className={styles.shell}>
      <header className={styles.header}>
        <h1 className={styles.title}>{t('room.title', { id })}</h1>
        <button className={styles.copyButton} onClick={onCopyLink}>
          {t('room.copyLink')}
        </button>
      </header>

      {/* Connection state banner (T069/T070) */}
      <ConnectionStatus />

      {blocker?.kind === 'error' && <div className={styles.banner}>{blocker.message}</div>}

      {/* NameGate: shown when room exists but no display name has been provided yet */}
      {showGate && <NameGate onSubmit={onGateSubmit} />}

      {/* Permission-denied panel (T066/T067): shown while in the room but media was denied */}
      {localMediaError && (
        <div className={styles.permissionPanel}>
          <p className={styles.permissionText}>
            {localMediaError === 'denied'
              ? t('permissions.denied')
              : t('permissions.requested')}
          </p>
          <button
            className={styles.permissionBtn}
            onClick={() => { void onRetryMedia() }}
            disabled={retrying}
          >
            {t('permissions.tryAgain')}
          </button>
        </div>
      )}

      <VideoGrid
        localStream={localStream}
        localPeerId={localPeerId}
        localLabel={localLabel ?? ''}
        localMediaState={localMediaState}
        peers={peers}
      />

      {/* Media controls bar (T081): only once fully connected — before that the
          WS may not be open, so a toggle's sendNotification would throw while
          local state flipped optimistically, diverging from the server. */}
      {connectionStatus === 'connected' && localPeerId && (
        <Controls
          localMediaState={localMediaState}
          onToggleMic={onToggleMic}
          onToggleCam={onToggleCam}
          onLeave={onLeave}
          remotePeerCount={peers.length}
          videoInputCount={videoInputCount}
          onSwitchCamera={() => { void onSwitchCamera() }}
        />
      )}

      {toast && <div className={styles.toast}>{toast}</div>}
    </main>
  )
}
